import React, { useState, useEffect } from 'react';
import { ContractClient } from '../contract';
import { ReadOnlyContractClient } from '../contract-readonly';

interface KYCSubmissionModalProps {
  isOpen: boolean;
  onClose: () => void;
  countryCode: string;
  countryName: string;
  contractClient: ContractClient | null;
  readOnlyClient: ReadOnlyContractClient;
  walletAddress: string | null;
  onKYCSubmitted?: () => void;
}

interface KYCFormData {
  // Natural Person Fields (SEP-0009)
  familyName: string;
  givenName: string;
  additionalName: string;
  addressCountryCode: string; // ISO 3166-1 alpha-3
  stateOrProvince: string;
  city: string;
  postalCode: string;
  address: string; // Full address as multi-line string
  mobileNumber: string; // E.164 format
  emailAddress: string;
  birthDate: string; // ISO 8601 date format (YYYY-MM-DD)
  birthPlace: string;
  birthCountryCode: string; // ISO 3166-1 alpha-3
  taxId: string;
  taxIdName: string;
  occupation: string; // ISCO08 code (as string for input)
  employerName: string;
  employerAddress: string; // SEP-0009: employer address
  languageCode: string; // ISO 639-1
  idType: string; // passport, drivers_license, id_card, etc.
  idCountryCode: string; // ISO 3166-1 alpha-3
  idNumber: string;
  idIssueDate: string; // ISO 8601 date
  idExpirationDate: string; // ISO 8601 date
  sex: string; // male, female, other
  referralId: string;
}

const KYCSubmissionModal: React.FC<KYCSubmissionModalProps> = ({
  isOpen,
  onClose,
  countryCode,
  countryName,
  contractClient,
  readOnlyClient,
  walletAddress,
  onKYCSubmitted
}) => {
  const [formData, setFormData] = useState<KYCFormData>({
    familyName: '',
    givenName: '',
    additionalName: '',
    addressCountryCode: '',
    stateOrProvince: '',
    city: '',
    postalCode: '',
    address: '',
    mobileNumber: '',
    emailAddress: '',
    birthDate: '',
    birthPlace: '',
    birthCountryCode: '',
    taxId: '',
    taxIdName: '',
    occupation: '',
    employerName: '',
    employerAddress: '',
    languageCode: '',
    idType: '',
    idCountryCode: '',
    idNumber: '',
    idIssueDate: '',
    idExpirationDate: '',
    sex: '',
    referralId: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [existingKYC, setExistingKYC] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (isOpen && walletAddress && countryCode) {
      loadExistingKYC();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, walletAddress, countryCode]);

  const loadExistingKYC = async () => {
    if (!walletAddress || !countryCode) return;
    
    setIsLoading(true);
    try {
      const kycData = await readOnlyClient.getKYCData(walletAddress, countryCode);
      if (kycData) {
        setExistingKYC(kycData);
        // Pre-fill form with existing data
        setFormData({
          familyName: kycData.familyName || '',
          givenName: kycData.givenName || '',
          additionalName: kycData.additionalName || '',
          addressCountryCode: kycData.addressCountryCode || '',
          stateOrProvince: kycData.stateOrProvince || '',
          city: kycData.city || '',
          postalCode: kycData.postalCode || '',
          address: kycData.address || '',
          mobileNumber: kycData.mobileNumber || '',
          emailAddress: kycData.emailAddress || '',
          birthDate: kycData.birthDate || '',
          birthPlace: kycData.birthPlace || '',
          birthCountryCode: kycData.birthCountryCode || '',
          taxId: kycData.taxId || '',
          taxIdName: kycData.taxIdName || '',
          occupation: kycData.occupation?.toString() || '',
          employerName: kycData.employerName || '',
          employerAddress: kycData.employerAddress || '',
          languageCode: kycData.languageCode || '',
          idType: kycData.idType || '',
          idCountryCode: kycData.idCountryCode || '',
          idNumber: kycData.idNumber || '',
          idIssueDate: kycData.idIssueDate || '',
          idExpirationDate: kycData.idExpirationDate || '',
          sex: kycData.sex || '',
          referralId: kycData.referralId || ''
        });
      }
    } catch (error: any) {
      // console.error('[KYCModal] Failed to load existing KYC:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (field: keyof KYCFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!contractClient || !walletAddress) {
      setError('Wallet not connected');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      // Convert occupation to number if provided
      const occupationNum = formData.occupation ? parseInt(formData.occupation, 10) : undefined;
      
      await contractClient.submitKYCData(countryCode, {
        familyName: formData.familyName || undefined,
        givenName: formData.givenName || undefined,
        additionalName: formData.additionalName || undefined,
        addressCountryCode: formData.addressCountryCode || undefined,
        stateOrProvince: formData.stateOrProvince || undefined,
        city: formData.city || undefined,
        postalCode: formData.postalCode || undefined,
        address: formData.address || undefined,
        mobileNumber: formData.mobileNumber || undefined,
        emailAddress: formData.emailAddress || undefined,
        birthDate: formData.birthDate || undefined,
        birthPlace: formData.birthPlace || undefined,
        birthCountryCode: formData.birthCountryCode || undefined,
        taxId: formData.taxId || undefined,
        taxIdName: formData.taxIdName || undefined,
        occupation: occupationNum,
        employerName: formData.employerName || undefined,
        employerAddress: formData.employerAddress || undefined,
        languageCode: formData.languageCode || undefined,
        idType: formData.idType || undefined,
        idCountryCode: formData.idCountryCode || undefined,
        idNumber: formData.idNumber || undefined,
        idIssueDate: formData.idIssueDate || undefined,
        idExpirationDate: formData.idExpirationDate || undefined,
        sex: formData.sex || undefined,
        referralId: formData.referralId || undefined
      });

      alert('KYC data submitted successfully! It will be reviewed by an admin.');
      if (onKYCSubmitted) {
        onKYCSubmitted();
      }
      onClose();
    } catch (error: any) {
      setError(error.message || 'Failed to submit KYC data');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 10000
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '8px',
        padding: '24px',
        maxWidth: '600px',
        maxHeight: '90vh',
        overflowY: 'auto',
        width: '90%',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h2 style={{ margin: 0 }}>Submit KYC Data - {countryName}</h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '24px',
              cursor: 'pointer',
              color: '#666'
            }}
          >
            ×
          </button>
        </div>

        {existingKYC && (
          <div style={{
            padding: '12px',
            marginBottom: '20px',
            backgroundColor: existingKYC.kycStatus === 'approved' ? '#d4edda' : existingKYC.kycStatus === 'rejected' ? '#f8d7da' : '#fff3cd',
            borderRadius: '4px',
            border: `1px solid ${existingKYC.kycStatus === 'approved' ? '#c3e6cb' : existingKYC.kycStatus === 'rejected' ? '#f5c6cb' : '#ffeaa7'}`
          }}>
            <strong>Current Status:</strong> {existingKYC.kycStatus.toUpperCase()}
            {existingKYC.kycVerifiedAt && (
              <div style={{ fontSize: '12px', marginTop: '4px' }}>
                Verified: {new Date(existingKYC.kycVerifiedAt * 1000).toLocaleString()}
              </div>
            )}
          </div>
        )}

        {error && (
          <div style={{
            padding: '12px',
            marginBottom: '20px',
            backgroundColor: '#f8d7da',
            color: '#721c24',
            borderRadius: '4px'
          }}>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>
              Given Name (First Name) *
            </label>
            <input
              type="text"
              value={formData.givenName}
              onChange={(e) => handleInputChange('givenName', e.target.value)}
              required
              style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
            />
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>
              Family Name (Last Name) *
            </label>
            <input
              type="text"
              value={formData.familyName}
              onChange={(e) => handleInputChange('familyName', e.target.value)}
              required
              style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
            />
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>
              Email Address *
            </label>
            <input
              type="email"
              value={formData.emailAddress}
              onChange={(e) => handleInputChange('emailAddress', e.target.value)}
              required
              style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
            />
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>
              Mobile Number (E.164 format, e.g., +1234567890)
            </label>
            <input
              type="tel"
              value={formData.mobileNumber}
              onChange={(e) => handleInputChange('mobileNumber', e.target.value)}
              placeholder="+1234567890"
              style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
            />
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>
              Date of Birth (YYYY-MM-DD)
            </label>
            <input
              type="date"
              value={formData.birthDate}
              onChange={(e) => handleInputChange('birthDate', e.target.value)}
              style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
            />
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>
              Address (Full address as multi-line string)
            </label>
            <textarea
              value={formData.address}
              onChange={(e) => handleInputChange('address', e.target.value)}
              rows={3}
              style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
            />
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>
              City
            </label>
            <input
              type="text"
              value={formData.city}
              onChange={(e) => handleInputChange('city', e.target.value)}
              style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
            />
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>
              State/Province
            </label>
            <input
              type="text"
              value={formData.stateOrProvince}
              onChange={(e) => handleInputChange('stateOrProvince', e.target.value)}
              style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
            />
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>
              Postal Code
            </label>
            <input
              type="text"
              value={formData.postalCode}
              onChange={(e) => handleInputChange('postalCode', e.target.value)}
              style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
            />
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>
              ID Type
            </label>
            <select
              value={formData.idType}
              onChange={(e) => handleInputChange('idType', e.target.value)}
              style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
            >
              <option value="">Select ID Type</option>
              <option value="passport">Passport</option>
              <option value="drivers_license">Driver's License</option>
              <option value="id_card">ID Card</option>
            </select>
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>
              ID Number
            </label>
            <input
              type="text"
              value={formData.idNumber}
              onChange={(e) => handleInputChange('idNumber', e.target.value)}
              style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
            />
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>
              Tax ID / SSN
            </label>
            <input
              type="text"
              value={formData.taxId}
              onChange={(e) => handleInputChange('taxId', e.target.value)}
              style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
            />
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>
              Employer Name
            </label>
            <input
              type="text"
              value={formData.employerName}
              onChange={(e) => handleInputChange('employerName', e.target.value)}
              style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
            />
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>
              Employer Address
            </label>
            <textarea
              value={formData.employerAddress}
              onChange={(e) => handleInputChange('employerAddress', e.target.value)}
              rows={2}
              style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
              placeholder="Full employer address"
            />
          </div>

          <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
            <button
              type="submit"
              disabled={isSubmitting || isLoading}
              style={{
                flex: 1,
                padding: '12px',
                backgroundColor: isSubmitting ? '#ccc' : '#007bff',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: isSubmitting ? 'not-allowed' : 'pointer',
                fontSize: '16px',
                fontWeight: '500'
              }}
            >
              {isSubmitting ? 'Submitting...' : 'Submit KYC Data'}
            </button>
            <button
              type="button"
              onClick={onClose}
              style={{
                padding: '12px 24px',
                backgroundColor: '#6c757d',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '16px'
              }}
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default KYCSubmissionModal;
